import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import styles from './datedlist.module.scss'
import { Col, Grid, Row } from '../../components'
import RowDate from './RowDate'
import More from './More'
import PropTypes from 'prop-types'
import Download from './Download'
import Skeleton from './Skeleton'

const DatedList = ({
  className = '',
  items = [],
  loading = false,
  loaded = false,
  handleGetMoreItems = () => {},
  downloadText = false,
  downloadLink = false,
  moreButtonLabel = null,
  itemComponent = () => {},
  pagination = {},
  emptyComponent = null,
  loadFailed = false,
  l10n = {
    formatFunction: (
      dateTimestamp,
      formatDate,
      { today = 'Today', yesterday = 'Yesterday' },
    ) => new Date(dateTimestamp).toLocaleDateString(),
    today: 'Today',
    yesterday: 'Yesterday',
    formatDates: { short: 'd MMMM', long: 'd MMMM yyyy' },
  },
}) => {
  const [moreButtonShown, toggleMoreButton] = useState(false)
  const Item = useMemo(() => itemComponent, [itemComponent])

  useEffect(() => {
    toggleMoreButton(pagination.total > items.length)
  }, [pagination, toggleMoreButton, items])

  let groupDate = null

  const EmptyComponent = emptyComponent ? emptyComponent : null

  if (!loading && (loaded || loadFailed) && items?.length === 0) {
    return EmptyComponent
  }

  if (!loaded && loading) {
    return <Skeleton />
  }

  return (
    <div className={clsx(styles['dated-list'], className)}>
      <Grid className={styles['dated-list__grid']}>
        {items.map((item, index) => {
          let showDate = false
          const currentDate = new Date(item.date * 1000)
          const currentDateWithoutHour =
            new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate(),
            ).getTime() / 1000

          if (groupDate !== currentDateWithoutHour) {
            showDate = true
            groupDate = currentDateWithoutHour
          }

          return (
            <React.Fragment key={item.id}>
              {showDate && (
                <Row
                  alignItems="center"
                  justifyContent="between"
                  className={styles['dated-list__item-heading']}
                >
                  <Col
                    sm="auto"
                    xs="auto"
                    className={styles['dated-list__col']}
                  >
                    <RowDate date={item.date} l10n={l10n} />
                  </Col>
                  {index === 0 && downloadText && (
                    <Col sm="auto" xs="auto">
                      <Download message={downloadText} link={downloadLink} />
                    </Col>
                  )}
                </Row>
              )}
              <Item {...item} />
            </React.Fragment>
          )
        })}
      </Grid>
      {moreButtonShown && (
        <More
          loading={loading}
          handleGetMoreItems={handleGetMoreItems}
          text={moreButtonLabel}
        />
      )}
    </div>
  )
}

DatedList.propTypes = {
  itemComponent: PropTypes.func,
  className: PropTypes.string,
  items: PropTypes.array,
  pagination: PropTypes.object,
  loading: PropTypes.bool,
  loaded: PropTypes.bool,
  loadFailed: PropTypes.bool,
  handleGetMoreItems: PropTypes.func,
  downloadText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  downloadLink: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  l10n: PropTypes.shape({
    formatFunction: PropTypes.func,
    today: PropTypes.string,
    yesterday: PropTypes.string,
    formatDates: PropTypes.object,
  }),
  moreButtonLabel: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.string,
  ]),
}

export default DatedList
